// ManifestChoice — "which carton is this going in?"
//
// A vendor return ships on a MANIFEST: one numbered document listing everything
// going back to one vendor in one box. The common shop reality is that a
// manifest is already open — somebody started it this morning with three dead
// boards on it — and the worst outcome is a second manifest for the same vendor
// sitting beside the first, because then two half-full cartons go out.
//
// So this component's whole opinion is: if there IS an open draft for this
// vendor, JOIN IT. It preselects the newest one on load (`defaultToDraft`,
// default true) rather than making the person notice and choose, and "Start a
// new manifest" stays one click away for the case where a separate shipment is
// genuinely wanted.
//
// ── A CHOICE OF ONE IS NOT A CHOICE ──────────────────────────────────────────
// With no open draft there is exactly one outcome, so the radio group is a
// control whose only option is already taken: it asks a question, costs a tab
// stop, and cannot be answered any other way. A vendor with no draft gets a
// SENTENCE saying what will happen instead. The fieldset exists only where
// there is something to pick, which is the same rule the value obeys: `value`
// stays 'new' either way, so the parent's submit is unchanged.
//
// The preselect fires ONCE per vendor. Firing it again would drag somebody who
// deliberately picked "new" back onto a draft on the next render.

import React, { useEffect, useRef, useState } from 'react';
import { Alert } from '../../components/ui/Alert';
import FadeIn from '../../components/ui/FadeIn';
import { EmptyState } from '../../components/ui/EmptyState';
import { RadioGroup, RadioRow } from '../../components/ui/Radio';
import { Skeleton, SkeletonGroup } from '../../components/ui/Skeleton';
import { formatTimestamp } from '../../utils/date-utils';
import { useBoardReturnsApi, type ManifestRow } from './boardReturnsApi';

export interface ManifestChoiceProps {
  /** No vendor means nothing to look up: only "Start a new manifest" renders. */
  vendorId: string | null;
  value: 'new' | string;
  onChange: (value: 'new' | string) => void;
  /** Preselect the newest open draft when one turns up. See the header note. */
  defaultToDraft?: boolean;
  className?: string;
}

const NOTE = 'text-xs text-on-surface-variant';

/** Newest first, whatever order the server sent. The list route already sorts
 *  created_at desc, but "which draft do we join" is this component's promise
 *  and it should not rest on a clause in somebody else's query. */
function newestFirst(rows: ManifestRow[]): ManifestRow[] {
  return [...rows].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
}

/** Everything after the number in "VR-000042 · 3 lines · started Aug 20": how
 *  full it is and when it started, which is how somebody recognizes the one on
 *  their bench. Split out only so the number can wear the mono face while the
 *  label still reads as one sentence. */
function manifestSummary(row: ManifestRow): string {
  const lines = `${row.line_count} ${row.line_count === 1 ? 'line' : 'lines'}`;
  return ` · ${lines} · started ${formatTimestamp(row.created_at, 'MMM D')}`;
}

const ManifestChoice: React.FC<ManifestChoiceProps> = ({
  vendorId,
  value,
  onChange,
  defaultToDraft = true,
  className = '',
}) => {
  const api = useBoardReturnsApi();
  const [drafts, setDrafts] = useState<ManifestRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  // One preselect per vendor. The ref is reset by the fetch effect below, so
  // switching vendors gets a fresh preselect and a re-render does not.
  const defaulted = useRef(false);
  // onChange comes from a parent that rebuilds it every render; reading it out
  // of a ref keeps it out of the effect's deps, where it would refire the
  // preselect on every keystroke in the dialog above.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;

  const radioName = `manifest-choice-${vendorId || 'none'}`;

  useEffect(() => {
    defaulted.current = false;
    if (!vendorId) {
      setDrafts([]);
      setFailed(false);
      return;
    }
    let live = true;
    setLoading(true);
    setFailed(false);
    api
      .listManifests('draft', vendorId)
      .then((res) => {
        if (!live) return;
        const rows = newestFirst(res?.rows || []);
        setDrafts(rows);
        // The preselect, and the only place this component writes upward.
        if (defaultToDraft && rows.length > 0 && valueRef.current === 'new' && !defaulted.current) {
          defaulted.current = true;
          onChangeRef.current(rows[0].id);
        }
      })
      .catch(() => {
        if (live) {
          setDrafts([]);
          setFailed(true);
        }
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
    // api is rebuilt every render by useApi; including it would refetch forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId, defaultToDraft]);

  const vendorName = drafts[0]?.vendor_name || null;

  if (loading) {
    return (
      <div className={className} aria-busy="true">
        <FadeIn key="loading">
          <SkeletonGroup label="Looking for open manifests" className="space-y-1.5">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </SkeletonGroup>
        </FadeIn>
      </div>
    );
  }

  // Nothing to join, so nothing to ask. The sentence says what pressing the
  // dialog's own button will do, which is the fact the radio group was carrying.
  if (drafts.length === 0) {
    return (
      <div className={className}>
        <FadeIn key="empty">
          {failed ? (
            <Alert tone="warning" variant="text" live="alert" className="!text-xs !leading-4">
              Could not load open manifests. A new one will be started.
            </Alert>
          ) : (
            <EmptyState variant="line" dense>
              {vendorId
                ? 'No open manifest for this vendor. A new one will be started.'
                : 'A new manifest will be started.'}
            </EmptyState>
          )}
        </FadeIn>
      </div>
    );
  }

  return (
    <div className={className}>
      <FadeIn key="drafts">
      <RadioGroup<string> label="Which manifest" value={value} onChange={onChange} name={radioName}>
        <p className={NOTE}>
          {vendorName ? `Open drafts for ${vendorName}` : 'Open drafts for this vendor'}
        </p>
        {drafts.map((row) => (
          <RadioRow key={row.id} value={row.id}>
            <span className="font-mono">{row.manifest_number}</span>
            <span className="text-on-surface-variant">{manifestSummary(row)}</span>
          </RadioRow>
        ))}
        <RadioRow value="new">Start a new manifest</RadioRow>
      </RadioGroup>
      </FadeIn>
    </div>
  );
};

export default ManifestChoice;
